/**
 * 中收业务相关接口类型定义
 * YAPI 分类: https://yapi.hellobike.cn/project/5967/interface/api/cat_63499
 */

/**
 * from 来源枚举（统一维护）
 */
export enum CreditMallFromEnum {
  /** 首页分期商城模块 */
  HOME_PAGE = 'finance_shopMall_home',
  /** 授信失败页分期商城模块 */
  CREDIT_FAIL_PAGE = 'finance_shopMall_credit_failed',
  /** 我的页分期商城去逛逛入口 */
  MY_PROFILE_STROLL = 'finance_shopMall_myProfile_stroll',
  /** 臻有钱初筛失败页协议页 */
  FINANCE_SCREENING_AGREEMENT = 'finance_screening_agreement',
  /** 臻有钱贷超半流程页 */
  FINANCE_SEMI_PROCESS = 'finance_shopMall_screening_failed',
  /** 臻有钱提现结果协议页 */
  FINANCE_WITHDRAW_AGREEMENT = 'finance_cashoutshoppingmall_agreement',
}

/**
 * 协议样式枚举（中收业务内维护，不依赖外部模块）
 */
export enum AgreementStyleEnum {
  /** 方案1 - A 内嵌 */
  SCHEME_A = 'A',
  /** 方案2 - B 全屏 */
  SCHEME_B = 'B',
}

/**
 * 协议内容类型枚举
 * 1: PDF
 * 2: 其他（如 HTML）
 */
export enum ContractContentTypeEnum {
  PDF = 1,
  ELSE = 2,
}

/**
 * 协议查询场景枚举
 * CREDIT(1, "credit", "授信阶段协议")
 * BIND_CARD(2, "bindCard", "绑卡阶段协议")
 * LOAN_APPLY(3, "loanApply", "借款阶段协议")
 * REPAY(4, "repay", "还款阶段协议")
 */
export enum MiddleIncomeContractSceneEnum {
  /** 授信阶段协议 */
  CREDIT = 'credit',
  /** 绑卡阶段协议 */
  BIND_CARD = 'bindCard',
  /** 借款阶段协议 */
  LOAN_APPLY = 'loanApply',
  /** 还款阶段协议 */
  REPAY = 'repay',
}

/**
 * 1. 中收授信结果查询接口
 * @yapi https://yapi.hellobike.cn/project/5967/interface/api/427809
 * @description 查询授信结果，支持商城和贷超授信结果查询。前端轮询调用此接口获取最新授信状态。
 */
export interface MiddleIncomeCreditResultParams {
  /** 授信任务ID。老用户可为空，新用户必填 */
  taskId?: string;
  /** 场景标识。例如：YXZH-营销召回 */
  scene?: string;
  /** 来源标识（商城原字段） */
  from?: CreditMallFromEnum;
  /** 客户端版本号 */
  version: string;
}

/** 页面状态枚举 */
export enum PageStatusEnum {
/** 初始化 */
  INIT = 'INIT',
  /** 成功 */
  SUCCEEDED = 'SUCCEEDED',
  /** 失败 */
  FAILED = 'FAILED',
  /** 处理中 */
  PROCESSING = 'PROCESSING',
}

/** 业务类型枚举 */
export enum BusinessTypeEnum {
  /** 商城授信 */
  SHOPPING_MALL = 'shoppingMall',
  /** 贷超授信 */
  OVER_LOAN = 'overLoan',
}

/** 贷超授信产品信息（保留用于兼容，新接口已不再返回） */
export interface OverLoanCreditProduct {
  /** 产品ID */
  commodityId: string;
  /** 产品名称 */
  commodityName: string;
  /** 图标URL */
  iconUrl: string;
  /** 贷款额度 */
  loanCeiling: number;
  /** 贷款期数 */
  loanPeriods: number[];
  /** 参考利率 */
  referenceInterestValue: string;
  /** 跳转URL */
  jumpUrl: string;
  /** 任务ID */
  taskId: string;
  /** 状态 SUCCEEDED/FAILED/PROCESSING */
  status: PageStatusEnum;
  /** 申请模式 serial/parallel */
  applyMode: string;
  /** 排序 */
  sort: number;
}

export interface MiddleIncomeCreditResultResponse {
  /** 授信状态。SUCCEEDED-授信成功；PROCESSING-申请中；FAILED-授信失败 */
  status?: PageStatusEnum;
  /** 跳转地址。授信成功时返回H5商城地址或贷超产品页 */
  jumpUrl?: string;
  /** 授信金额。成功时返回授信额度，失败或申请中时可能为0或空 */
  creditAmount?: string;
  /** 业务类型。shoppingMall-商城授信；overLoan-贷超授信 */
  businessType: BusinessTypeEnum;
}

/**
 * 2. 中收业务统一分发接口
 * @yapi https://yapi.hellobike.cn/project/5967/interface/api/427167
 * @description 中收业务统一分发接口，根据业务场景返回可用的商城和贷超产品列表及申请策略
 */
export interface MiddleIncomeDispatchParams {
  /** 页面场景，用于标识在哪个业务场景调用分发接口；提现结果页：loanResultPageDispatch */
  pageScene?: string;
  /** 资料是否不完整，true 不完整，false 完整 */
  incompleteInfo?: boolean;
  /** 业务信息 */
  businessInfo?: {
    /** 贷超业务使用：业务场景 NO_CHANNEL_AVAILABLE-无渠道分发 PURE_DC-纯半流程 */
    scene?: string;
    /** 商城业务使用，来源说明 */
    from?: CreditMallFromEnum;
  };
}

/** 商城/贷超商品信息 */
export interface CommodityItem {
  /** 图标 */
  icon: string;
  /** 产品ID */
  commodityId: string;
  /** 产品名称 */
  commodityName: string;
  /** 商品展示顺序 */
  displayPosition: string;
}

export interface MiddleIncomeDispatchResponse {
  /** 商城分发结果 */
  shoppingMallCommodities?: CommodityItem[];
  /** 贷超分发结果 */
  overLoanCommodities?: CommodityItem[];
  /** 贷超业务串并行模式 serial/parallel */
  applyMode?: string;
  /** 申请优先级列表 ["shoppingMall","overLoan"]或["all"] */
  priorityLevel?: string[];
  /** 协议展示样式，A：结果页内嵌协议，B：整屏的协议页 */
  agreementStyle?: AgreementStyleEnum;
}

/**
 * 3. 中收授信申请接口
 * @yapi https://yapi.hellobike.cn/project/5967/interface/api/427154
 * @description 根据编排策略调用授信申请，支持可配置的串行/并行策略
 */
export interface MiddleIncomeCreditApplyParams {
  /** 贷超的产品列表 */
  overLoanCommodityList?: string[];
  /** 商城的产品列表 */
  shoppingMallCommodityList?: string[];
  /** 城市信息 */
  cityInfoJson?: Record<string, unknown>;
  /** 设备信息 */
  deviceJson?: Record<string, unknown>;
  /** 场景标识 */
  scene?: string;
  /** 资料是否不完整，true 不完整，false 完整 */
  incompleteInfo?: boolean;
  /** 贷超撞库模式 serial/parallel */
  applyMode?: string;
  /** 业务串行优先级 ["shoppingMall","overLoan"]["all"] */
  priorityLevel?: string[];
  /** 来源标识(商城原来字段) */
  from?: CreditMallFromEnum;
  /** 授信任务ID */
  taskId?: string;
}

export interface MiddleIncomeCreditApplyResponse {
  /** 授信任务ID */
  taskId: string;
  /** 业务错误码 */
  businessErrorCode?: number;
}

/**
 * 4. 中收协议查询接口
 * @yapi https://yapi.hellobike.cn/project/5967/interface/api/427153
 * @description 并发调用商城和贷超协议查询接口，返回聚合协议列表
 */
export interface MiddleIncomeContractListParams {
  /**
   * 场景（非必填）
   * CREDIT(1, "credit", "授信阶段协议"), BIND_CARD(2, "bindCard", "绑卡阶段协议"),
   * LOAN_APPLY(3, "loanApply", "借款阶段协议"), REPAY(4, "repay", "还款阶段协议")
   */
  scene?: MiddleIncomeContractSceneEnum | string;
  /** 贷超产品列表 */
  overLoanCommoditys?: string[];
  /** 商城产品列表 */
  shoppingMallCommoditys?: string[];
}

/** 协议项 */
export interface ContractItemDetail {
  /** 协议URL */
  url: string;
  /** 协议名称 */
  name: string;
  /** 协议类型（1-PDF，2-其他） */
  type?: ContractContentTypeEnum;
}

/** 产品协议列表 */
export interface ProductContractList {
  /** 产品ID（非必填） */
  productId?: string;
  /** 产品名称（非必填） */
  productName?: string;
  /** 产品 icon（非必填） */
  productIcon?: string;
  /** 协议列表 */
  contractList: ContractItemDetail[];
}

export interface MiddleIncomeContractListResponse {
  /** 贷超的协议列表 */
  overLoanContractList?: ProductContractList[];
  /** 商城的协议列表 */
  shoppingMallContractList?: ProductContractList[];
}
